import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BackofficeSessionService } from '../core/backoffice-session.service';
import {
  BackofficeTournamentRegistrationItem
} from '../core/backoffice-tournament-registrations-api.types';
import { BackofficeTournamentRegistrationsApiService } from '../core/backoffice-tournament-registrations-api.service';
import { BackofficeTournamentItem } from '../core/backoffice-tournaments-api.types';
import { BackofficeTournamentsApiService } from '../core/backoffice-tournaments-api.service';
import { I18nService } from '../core/i18n.service';

@Component({
  selector: 'app-backoffice-tournament-registrations-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, DatePipe],
  template: `
    <section class="page">
      <a routerLink="/backoffice/tournaments" class="back-link">{{ t('bo.registrations.back') }}</a>
      <h1>{{ t('bo.registrations.title') }}</h1>

      <form (ngSubmit)="openTournament()" #form="ngForm" class="toolbar card">
        <label>{{ t('bo.registrations.tournament') }}
          <select name="tournamentId" required [(ngModel)]="selectedTournamentId">
            <option [ngValue]="null">{{ t('bo.registrations.selectTournament') }}</option>
            @for (tournament of tournaments(); track tournament.id) {
              <option [ngValue]="tournament.id">{{ tournament.name }} ({{ tournament.slug }})</option>
            }
          </select>
        </label>
        <button type="submit" [disabled]="!form.valid || tournamentsLoading()">{{ t('bo.registrations.load') }}</button>
        <a routerLink="/backoffice/session">{{ t('bo.registrations.session') }}</a>
      </form>

      @if (tournamentsLoading()) {
        <p class="state">{{ t('bo.registrations.tournamentsLoading') }}</p>
      }

      @if (hasLoaded() && validatedRegistrationCount() >= 2) {
        <section class="wheel-toolbar card">
          <div>
            <p class="wheel-title">{{ t('bo.registrations.wheelTitle') }}</p>
            <p class="state">{{ t('bo.registrations.wheelSubtitle') }}</p>
          </div>
          <button
            type="button"
            [disabled]="loading() || wheelLoading()"
            (click)="runWheel()"
          >
            {{
              wheelLoading()
                ? t('bo.registrations.wheelRunning')
                : t('bo.registrations.wheelRun')
            }}
          </button>
        </section>
      }

      @if (loading()) {
        <p class="state">{{ t('bo.registrations.loading') }}</p>
      } @else if (errorMessage()) {
        <p class="state state--error">{{ errorMessage() }}</p>
      } @else if (registrations().length === 0 && hasLoaded()) {
        <article class="empty">
          <p><strong>{{ t('bo.registrations.emptyTitle') }}</strong></p>
          <p class="state">{{ t('bo.registrations.emptySubtitle') }}</p>
        </article>
      } @else if (registrations().length > 0) {
        <ul class="list">
          @for (registration of registrations(); track registration.id) {
            <li class="item">
              <p>
                <strong>{{ registration.team_name }}</strong>
                <span class="status">{{ formatRegistrationStatus(registration.status) }}</span>
              </p>
              <p>{{ t('bo.registrations.submittedAt', { value: (registration.submitted_at | date: 'short') ?? '' }) }}</p>
              @if (registration.checked_in_at) {
                <p class="state state--success">
                  {{ t('bo.registrations.checkedInAt', { value: (registration.checked_in_at | date: 'short') ?? '' }) }}
                </p>
              } @else {
                <p class="state">{{ formatCheckInState(registration.check_in_state) }}</p>
              }

              @if (registration.decisions.length > 0) {
                <p class="state">
                  {{ t('bo.registrations.latestDecision', {
                    value: formatRegistrationStatus(registration.decisions[0].to_status),
                    date: (registration.decisions[0].created_at | date: 'short') ?? ''
                  }) }}
                </p>
              }

              @if (registration.status === 'validated') {
                <p class="state">{{ t('bo.registrations.wheelOrderValue', { value: registration.wheel_order_number ?? '-' }) }}</p>

                <label class="seed-label">
                  {{ t('bo.registrations.seed') }}
                  <div class="seed-row">
                    <input
                      type="number"
                      min="1"
                      [ngModel]="seedValue(registration)"
                      (ngModelChange)="updateSeedValue(registration.id, $event)"
                      [ngModelOptions]="{ standalone: true }"
                      [disabled]="seedLoadingId() === registration.id || manualSlotLoadingId() === registration.id || decisionLoadingId() === registration.id || checkInLoadingId() === registration.id"
                    />
                    <button
                      type="button"
                      class="button-secondary"
                      [disabled]="seedLoadingId() === registration.id || manualSlotLoadingId() === registration.id || decisionLoadingId() === registration.id || checkInLoadingId() === registration.id"
                      (click)="saveSeed(registration)"
                    >
                      {{
                        seedLoadingId() === registration.id
                          ? t('bo.registrations.seedSaving')
                          : t('bo.registrations.seedSave')
                      }}
                    </button>
                    @if (seedSavedId() === registration.id) {
                      <span class="save-feedback">{{ t('bo.registrations.saved') }}</span>
                    }
                  </div>
                </label>

                <label class="seed-label">
                  {{ t('bo.registrations.manualSlot') }}
                  <div class="seed-row">
                    <input
                      type="number"
                      min="1"
                      [ngModel]="manualSlotValue(registration)"
                      (ngModelChange)="updateManualSlotValue(registration.id, $event)"
                      [ngModelOptions]="{ standalone: true }"
                      [disabled]="manualSlotLoadingId() === registration.id || seedLoadingId() === registration.id || decisionLoadingId() === registration.id || checkInLoadingId() === registration.id"
                    />
                    <button
                      type="button"
                      class="button-secondary"
                      [disabled]="manualSlotLoadingId() === registration.id || seedLoadingId() === registration.id || decisionLoadingId() === registration.id || checkInLoadingId() === registration.id"
                      (click)="saveManualSlot(registration)"
                    >
                      {{
                        manualSlotLoadingId() === registration.id
                          ? t('bo.registrations.manualSlotSaving')
                          : t('bo.registrations.manualSlotSave')
                      }}
                    </button>
                    @if (manualSlotSavedId() === registration.id) {
                      <span class="save-feedback">{{ t('bo.registrations.saved') }}</span>
                    }
                  </div>
                </label>
              }

              @if (registration.allowed_admin_target_statuses.length > 0) {
                <label class="note-label">
                  {{ t('bo.registrations.reason') }}
                  <textarea
                    [ngModel]="decisionReasonValue(registration.id)"
                    (ngModelChange)="updateDecisionReason(registration.id, $event)"
                    [ngModelOptions]="{ standalone: true }"
                  ></textarea>
                </label>

                <div class="actions">
                  @for (targetStatus of registration.allowed_admin_target_statuses; track targetStatus) {
                    <button
                      type="button"
                      [disabled]="decisionLoadingId() === registration.id || checkInLoadingId() === registration.id"
                      (click)="decide(registration, targetStatus)"
                    >
                      {{
                        decisionLoadingId() === registration.id
                          ? t('bo.registrations.saving')
                          : registrationActionLabel(targetStatus)
                      }}
                    </button>
                  }

                  @if (registration.can_check_in) {
                    <button
                      type="button"
                      class="button-secondary"
                      [disabled]="decisionLoadingId() === registration.id || checkInLoadingId() === registration.id"
                      (click)="checkIn(registration)"
                    >
                      {{
                        checkInLoadingId() === registration.id
                          ? t('bo.registrations.checkInLoading')
                          : t('bo.registrations.checkIn')
                      }}
                    </button>
                  }
                </div>
              } @else if (registration.can_check_in) {
                <div class="actions">
                  <button
                    type="button"
                    class="button-secondary"
                    [disabled]="checkInLoadingId() === registration.id"
                    (click)="checkIn(registration)"
                  >
                    {{
                      checkInLoadingId() === registration.id
                        ? t('bo.registrations.checkInLoading')
                        : t('bo.registrations.checkIn')
                    }}
                  </button>
                </div>
              }
            </li>
          }
        </ul>
      }
    </section>
  `,
  styles: `
    .page { padding: 1.2rem; max-width: 960px; margin: 0 auto; }
    .back-link { color: var(--accent); text-decoration: none; display: inline-block; margin-bottom: 0.8rem; font-weight: 600; }
    h1 { margin: 0 0 0.8rem; font-size: 1.45rem; color: var(--text-strong); }
    .toolbar { display: flex; gap: 0.6rem; align-items: end; margin-bottom: 0.8rem; flex-wrap: wrap; }
    .wheel-toolbar { display: flex; gap: 0.8rem; align-items: center; justify-content: space-between; margin-bottom: 0.8rem; flex-wrap: wrap; }
    .wheel-title { margin: 0 0 0.2rem; color: var(--text-strong); font-weight: 700; }
    .card { border: 1px solid var(--line-soft); border-radius: 10px; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); padding: 0.8rem; box-shadow: var(--shadow-soft); }
    label { display: grid; gap: 0.35rem; font-size: 0.92rem; color: #dbe9ff; }
    select, textarea, input[type="number"] { padding: 0.55rem; border: 1px solid var(--line-soft); border-radius: 8px; min-width: 260px; background: rgba(13, 21, 34, 0.9); color: var(--text-strong); font: inherit; }
    textarea { min-height: 72px; min-width: 100%; resize: vertical; }
    button { border: 1px solid rgba(138, 184, 255, 0.6); border-radius: 8px; padding: 0.55rem 0.8rem; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); color: #091325; font-weight: 700; }
    .button-secondary { background: rgba(47, 62, 88, 0.75); color: var(--text-strong); border: 1px solid var(--line-soft); }
    .list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.65rem; }
    .empty { border: 1px solid var(--line-soft); border-radius: 10px; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); padding: 0.75rem; box-shadow: var(--shadow-soft); }
    .item { border: 1px solid var(--line-soft); border-radius: 10px; padding: 0.75rem; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); box-shadow: var(--shadow-soft); }
    .item p { margin: 0.2rem 0; color: var(--text-muted); }
    .item strong, .empty strong { color: var(--text-strong); }
    .status { margin-left: 0.45rem; display: inline-block; font-size: 0.78rem; color: #dbe9ff; background: rgba(101, 145, 216, 0.24); border: 1px solid rgba(112, 164, 255, 0.44); border-radius: 999px; padding: 0.16rem 0.45rem; vertical-align: middle; }
    .state { color: var(--text-muted); }
    .state--error { color: var(--danger); }
    .state--success { color: var(--ok-text); }
    .actions { display: flex; gap: 0.45rem; flex-wrap: wrap; margin-top: 0.55rem; }
    .note-label { margin-top: 0.55rem; }
    .seed-label { margin-top: 0.55rem; }
    .seed-row { display: flex; gap: 0.45rem; align-items: center; flex-wrap: wrap; }
    .seed-row input { min-width: 96px; width: 112px; }
    .save-feedback { color: #8fcb9b; font-size: 0.9rem; font-weight: 600; }
    a { color: var(--accent); text-decoration: none; font-weight: 600; }
  `
})
export class BackofficeTournamentRegistrationsPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly backofficeSession = inject(BackofficeSessionService);
  private readonly api = inject(BackofficeTournamentRegistrationsApiService);
  private readonly tournamentsApi = inject(BackofficeTournamentsApiService);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  protected selectedTournamentId: number | null = null;
  protected readonly activeTournamentId = signal<number | null>(null);
  protected readonly tournaments = signal<BackofficeTournamentItem[]>([]);
  protected readonly tournamentsLoading = signal(false);
  protected readonly registrations = signal<BackofficeTournamentRegistrationItem[]>([]);
  protected readonly loading = signal(false);
  protected readonly hasLoaded = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly decisionLoadingId = signal<number | null>(null);
  protected readonly checkInLoadingId = signal<number | null>(null);
  protected readonly wheelLoading = signal(false);
  protected readonly seedLoadingId = signal<number | null>(null);
  protected readonly manualSlotLoadingId = signal<number | null>(null);
  protected readonly seedSavedId = signal<number | null>(null);
  protected readonly manualSlotSavedId = signal<number | null>(null);
  protected readonly decisionReasons = signal<Partial<Record<number, string>>>({});
  protected readonly seedValues = signal<Partial<Record<number, string>>>({});
  protected readonly manualSlotValues = signal<Partial<Record<number, string>>>({});
  private seedSavedTimer: ReturnType<typeof setTimeout> | null = null;
  private manualSlotSavedTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.loadTournaments();
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const fromRoute = Number(params.get('tournamentId'));
        if (fromRoute > 0) {
          this.selectedTournamentId = fromRoute;
          this.activeTournamentId.set(fromRoute);
          this.fetch(fromRoute);
          return;
        }

        this.activeTournamentId.set(null);
        this.registrations.set([]);
        this.hasLoaded.set(false);
      });
  }

  protected openTournament(): void {
    if (!this.selectedTournamentId || this.selectedTournamentId <= 0) return;
    void this.router.navigateByUrl(`/backoffice/tournaments/${this.selectedTournamentId}/registrations`);
  }

  protected updateDecisionReason(registrationId: number, value: string): void {
    this.decisionReasons.update((current) => ({ ...current, [registrationId]: value }));
  }

  protected decisionReasonValue(registrationId: number): string {
    return this.decisionReasons()[registrationId] ?? '';
  }

  protected updateSeedValue(registrationId: number, value: string | number | null): void {
    const normalized = value == null ? '' : String(value);
    this.seedValues.update((current) => ({ ...current, [registrationId]: normalized }));
    if (this.seedSavedId() === registrationId) {
      this.clearSeedSaved();
    }
  }

  protected seedValue(registration: BackofficeTournamentRegistrationItem): string {
    const cached = this.seedValues()[registration.id];
    if (typeof cached === 'string') return cached;
    return registration.seed_number != null ? String(registration.seed_number) : '';
  }

  protected updateManualSlotValue(registrationId: number, value: string | number | null): void {
    const normalized = value == null ? '' : String(value);
    this.manualSlotValues.update((current) => ({ ...current, [registrationId]: normalized }));
    if (this.manualSlotSavedId() === registrationId) {
      this.clearManualSlotSaved();
    }
  }

  protected manualSlotValue(registration: BackofficeTournamentRegistrationItem): string {
    const cached = this.manualSlotValues()[registration.id];
    if (typeof cached === 'string') return cached;
    return registration.manual_slot_number != null ? String(registration.manual_slot_number) : '';
  }

  protected validatedRegistrationCount(): number {
    return this.registrations().filter((registration) => registration.status === 'validated').length;
  }

  protected decide(registration: BackofficeTournamentRegistrationItem, status: string): void {
    const tournamentId = this.activeTournamentId();
    if (!tournamentId) return;

    this.decisionLoadingId.set(registration.id);
    this.errorMessage.set(null);
    const reason = (this.decisionReasons()[registration.id] ?? '').trim();

    this.api
      .decide(tournamentId, registration.id, status, reason || null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ registration: updated }) => {
          this.replaceRegistration(updated);
          this.decisionLoadingId.set(null);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          const apiError = error?.error?.error;
          const apiErrors = error?.error?.errors;
          this.errorMessage.set(
            (typeof apiError === 'string' && apiError) ||
              (Array.isArray(apiErrors) && apiErrors.length > 0 ? apiErrors.join(' | ') : this.t('bo.registrations.decisionError'))
          );
          this.decisionLoadingId.set(null);
        }
      });
  }

  protected checkIn(registration: BackofficeTournamentRegistrationItem): void {
    const tournamentId = this.activeTournamentId();
    if (!tournamentId) return;

    this.checkInLoadingId.set(registration.id);
    this.errorMessage.set(null);

    this.api
      .checkIn(tournamentId, registration.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ registration: updated }) => {
          this.replaceRegistration(updated);
          this.checkInLoadingId.set(null);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          const apiError = error?.error?.error;
          const apiErrors = error?.error?.errors;
          this.errorMessage.set(
            (typeof apiError === 'string' && apiError) ||
              (Array.isArray(apiErrors) && apiErrors.length > 0 ? apiErrors.join(' | ') : this.t('bo.registrations.checkInError'))
          );
          this.checkInLoadingId.set(null);
        }
      });
  }

  protected saveSeed(registration: BackofficeTournamentRegistrationItem): void {
    const tournamentId = this.activeTournamentId();
    if (!tournamentId) return;

    const rawValue = String(this.seedValues()[registration.id] ?? '').trim();
    const parsedSeed = rawValue ? Number(rawValue) : null;

    this.seedLoadingId.set(registration.id);
    this.clearSeedSaved();
    this.errorMessage.set(null);

    this.api
      .updateSeed(tournamentId, registration.id, Number.isFinite(parsedSeed as number) ? parsedSeed : null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ registration: updated }) => {
          this.replaceRegistration(updated);
          this.seedLoadingId.set(null);
          this.showSeedSaved(updated.id);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          const apiError = error?.error?.error;
          const apiErrors = error?.error?.errors;
          this.errorMessage.set(
            (typeof apiError === 'string' && apiError) ||
              (Array.isArray(apiErrors) && apiErrors.length > 0 ? apiErrors.join(' | ') : this.t('bo.registrations.seedError'))
          );
          this.seedLoadingId.set(null);
        }
      });
  }

  protected saveManualSlot(registration: BackofficeTournamentRegistrationItem): void {
    const tournamentId = this.activeTournamentId();
    if (!tournamentId) return;

    const rawValue = String(this.manualSlotValues()[registration.id] ?? '').trim();
    const parsedSlot = rawValue ? Number(rawValue) : null;

    this.manualSlotLoadingId.set(registration.id);
    this.clearManualSlotSaved();
    this.errorMessage.set(null);

    this.api
      .updateManualSlot(tournamentId, registration.id, Number.isFinite(parsedSlot as number) ? parsedSlot : null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ registration: updated }) => {
          this.replaceRegistration(updated);
          this.manualSlotLoadingId.set(null);
          this.showManualSlotSaved(updated.id);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          const apiError = error?.error?.error;
          const apiErrors = error?.error?.errors;
          this.errorMessage.set(
            (typeof apiError === 'string' && apiError) ||
              (Array.isArray(apiErrors) && apiErrors.length > 0 ? apiErrors.join(' | ') : this.t('bo.registrations.manualSlotError'))
          );
          this.manualSlotLoadingId.set(null);
        }
      });
  }

  protected runWheel(): void {
    const tournamentId = this.activeTournamentId();
    if (!tournamentId) return;

    this.wheelLoading.set(true);
    this.errorMessage.set(null);

    this.api
      .runWheel(tournamentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ registrations }) => {
          this.setRegistrations(registrations);
          this.wheelLoading.set(false);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          const apiError = error?.error?.error;
          const apiErrors = error?.error?.errors;
          this.errorMessage.set(
            (typeof apiError === 'string' && apiError) ||
              (Array.isArray(apiErrors) && apiErrors.length > 0 ? apiErrors.join(' | ') : this.t('bo.registrations.wheelError'))
          );
          this.wheelLoading.set(false);
        }
      });
  }

  protected formatRegistrationStatus(status: string): string {
    const labels: Record<string, string> = {
      submitted: this.t('bo.registrations.status.submitted'),
      to_fix: this.t('bo.registrations.status.toFix'),
      validated: this.t('bo.registrations.status.validated'),
      validated_conditionally: this.t('bo.registrations.status.validatedConditionally'),
      rejected: this.t('bo.registrations.status.rejected'),
      cancelled: this.t('bo.registrations.status.cancelled')
    };
    return labels[status] ?? status;
  }

  protected registrationActionLabel(targetStatus: string): string {
    const labels: Record<string, string> = {
      to_fix: this.t('bo.registrations.action.toFix'),
      validated: this.t('bo.registrations.action.validate'),
      validated_conditionally: this.t('bo.registrations.action.validateConditionally'),
      rejected: this.t('bo.registrations.action.reject'),
      cancelled: this.t('bo.registrations.action.cancel')
    };
    return labels[targetStatus] ?? targetStatus;
  }

  protected formatCheckInState(state: BackofficeTournamentRegistrationItem['check_in_state']): string {
    const labels: Record<BackofficeTournamentRegistrationItem['check_in_state'], string> = {
      checked_in: this.t('bo.registrations.checkIn.checkedIn'),
      eligible: this.t('bo.registrations.checkIn.eligible'),
      window_closed: this.t('bo.registrations.checkIn.closed'),
      not_eligible: this.t('bo.registrations.checkIn.notEligible')
    };
    return labels[state];
  }

  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }

  private loadTournaments(): void {
    this.tournamentsLoading.set(true);
    this.tournamentsApi
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tournaments }) => {
          const sorted = [...tournaments].sort((a, b) => a.name.localeCompare(b.name));
          this.tournaments.set(sorted);
          if (!this.selectedTournamentId && sorted.length > 0) {
            this.selectedTournamentId = sorted[0].id;
          }
          this.tournamentsLoading.set(false);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          if (error?.status === 403) {
            this.errorMessage.set(this.t('bo.registrations.forbiddenTournaments'));
            this.tournamentsLoading.set(false);
            return;
          }
          this.errorMessage.set(this.t('bo.registrations.loadTournamentsError'));
          this.tournamentsLoading.set(false);
        }
      });
  }

  private fetch(tournamentId: number): void {
    this.loading.set(true);
    this.errorMessage.set(null);
    this.hasLoaded.set(false);
    this.api
      .list(tournamentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ registrations }) => {
          this.setRegistrations(registrations);
          this.loading.set(false);
          this.hasLoaded.set(true);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          if (error?.status === 403) {
            this.errorMessage.set(this.t('bo.registrations.forbiddenList'));
            this.loading.set(false);
            this.hasLoaded.set(true);
            return;
          }
          const apiError = error?.error?.error;
          this.errorMessage.set(typeof apiError === 'string' ? apiError : this.t('bo.registrations.loadError'));
          this.loading.set(false);
          this.hasLoaded.set(true);
        }
      });
  }

  private replaceRegistration(updated: BackofficeTournamentRegistrationItem): void {
    this.registrations.update((items) =>
      items.map((item) => (item.id === updated.id ? updated : item))
    );
    this.seedValues.update((current) => ({
      ...current,
      [updated.id]: updated.seed_number != null ? String(updated.seed_number) : ''
    }));
    this.manualSlotValues.update((current) => ({
      ...current,
      [updated.id]: updated.manual_slot_number != null ? String(updated.manual_slot_number) : ''
    }));
  }

  private setRegistrations(registrations: BackofficeTournamentRegistrationItem[]): void {
    this.registrations.set(registrations);
    this.seedValues.set(
      registrations.reduce<Partial<Record<number, string>>>((acc, registration) => {
        acc[registration.id] = registration.seed_number != null ? String(registration.seed_number) : '';
        return acc;
      }, {})
    );
    this.manualSlotValues.set(
      registrations.reduce<Partial<Record<number, string>>>((acc, registration) => {
        acc[registration.id] = registration.manual_slot_number != null ? String(registration.manual_slot_number) : '';
        return acc;
      }, {})
    );
  }

  private showSeedSaved(registrationId: number): void {
    this.clearSeedSaved();
    this.seedSavedId.set(registrationId);
    this.seedSavedTimer = setTimeout(() => this.seedSavedId.set(null), 1800);
  }

  private clearSeedSaved(): void {
    if (this.seedSavedTimer) {
      clearTimeout(this.seedSavedTimer);
      this.seedSavedTimer = null;
    }
    this.seedSavedId.set(null);
  }

  private showManualSlotSaved(registrationId: number): void {
    this.clearManualSlotSaved();
    this.manualSlotSavedId.set(registrationId);
    this.manualSlotSavedTimer = setTimeout(() => this.manualSlotSavedId.set(null), 1800);
  }

  private clearManualSlotSaved(): void {
    if (this.manualSlotSavedTimer) {
      clearTimeout(this.manualSlotSavedTimer);
      this.manualSlotSavedTimer = null;
    }
    this.manualSlotSavedId.set(null);
  }

  private handleUnauthorized(): void {
    this.backofficeSession.clearToken();
    void this.router.navigateByUrl('/backoffice/session');
  }
}
